import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { useBackHandler } from '@/context/BackHandlerContext';

const ROOT_TAB_ROUTES = new Set(['/', '/tools', '/profile', '/login']);
const DOUBLE_TAP_MS = 2000;

export function isRootTabRoute(pathname: string): boolean {
  return ROOT_TAB_ROUTES.has(pathname);
}

export function getFallbackParentRoute(pathname: string): string | null {
  if (pathname.startsWith('/upload/')) {
    return '/upload';
  }

  if (pathname === '/upload') {
    return '/';
  }

  if (pathname.startsWith('/profile/')) {
    return '/profile';
  }

  if (pathname.startsWith('/tools/')) {
    return '/tools';
  }

  if (pathname.startsWith('/records/')) {
    return '/';
  }

  if (pathname.startsWith('/shared/')) {
    return '/';
  }

  if (pathname === '/peer-review') {
    return '/tools';
  }

  if (pathname.startsWith('/complete-profile/')) {
    return '/profile';
  }

  if (pathname === '/forgot-password') {
    return '/login';
  }

  return null;
}

export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const { invokeBackHandler } = useBackHandler();
  const lastBackPressRef = useRef(0);
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  const invokeBackHandlerRef = useRef(invokeBackHandler);

  useEffect(() => {
    locationRef.current = location;
    navigateRef.current = navigate;
    invokeBackHandlerRef.current = invokeBackHandler;
  });

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') {
      return undefined;
    }

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupListener = async () => {
      listenerHandle = await App.addListener('backButton', ({ canGoBack }) => {
        if (invokeBackHandlerRef.current()) {
          return;
        }

        const pathname = locationRef.current.pathname;

        if (canGoBack) {
          navigateRef.current(-1);
          return;
        }

        if (isRootTabRoute(pathname)) {
          const now = Date.now();

          if (now - lastBackPressRef.current < DOUBLE_TAP_MS) {
            lastBackPressRef.current = 0;
            void App.exitApp();
            return;
          }

          lastBackPressRef.current = now;
          toast('Press back again to exit');
          return;
        }

        const fallbackRoute = getFallbackParentRoute(pathname);

        if (fallbackRoute) {
          navigateRef.current(fallbackRoute, { replace: true });
          return;
        }

        navigateRef.current(-1);
      });
    };

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
  }, []);
}
